import { useMemo, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import {
  V2Alert,
  V2Button,
  V2Card,
  V2CardHeader,
  V2FormField,
  V2FormGrid,
} from '@sakhtar/design-system/react';
import { apiPost } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import type { StockBalancesResponse } from '../../lib/types/inventory.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import { useTenantSettings } from '../../lib/tenantSettings.js';
import type { PurchaseOrderListResponse } from '../../lib/types/procurement.js';
import { orderStatusOf } from '../../lib/types/procurement.js';
import type { SalesOrderListResponse } from '../../lib/types/crm.js';
import { orderStatusLabel } from '../crm/customer360Model.js';
import {
  STOCK_BALANCES_LIMIT,
  addQuantity,
  formatQuantity,
  normalizeDigits,
  parseWholeQuantity,
  quantitySign,
  subtractQuantity,
  wholePartText,
  type Quantity,
} from './inventoryDisplay.js';
import './inventory.css';

type Variant = 'receipt' | 'issue' | 'transfer';

interface LineDraft {
  productCode: string;
  warehouseCode: string;
  locationCode: string;
  lotCode: string;
  serialNumber: string;
  quantityNumerator: string;
  unitCostRial: string;
  counterWarehouseCode: string;
  counterLocationCode: string;
  poRowNumber?: number;
}

const EMPTY_LINE: LineDraft = {
  productCode: '',
  warehouseCode: '',
  locationCode: '',
  lotCode: '',
  serialNumber: '',
  quantityNumerator: '',
  unitCostRial: '',
  counterWarehouseCode: '',
  counterLocationCode: '',
};

const COPY: Record<
  Variant,
  { title: string; itemsTitle: string; submitLabel: string; endpoint: string }
> = {
  receipt: {
    title: 'رسید جدید',
    itemsTitle: 'اقلام رسید',
    submitLabel: 'ثبت رسید',
    endpoint: 'receipts',
  },
  issue: {
    title: 'حواله جدید',
    itemsTitle: 'اقلام خروج',
    submitLabel: 'ثبت خروج',
    endpoint: 'issues',
  },
  transfer: {
    title: 'انتقال جدید',
    itemsTitle: 'اقلام انتقال',
    submitLabel: 'ثبت انتقال',
    endpoint: 'transfers',
  },
};

/**
 * Shared create-form for receipt/issue/transfer — real endpoints
 * (POST .../inventory/{receipts,issues,transfers}), resolved by
 * product/warehouse/location CODE server-side (resolveMovementLine in
 * apps/api/src/inventory/resolve-refs.ts), not by opaque IDs. Location is
 * now a real picker (BUG-11: `GET master-data/locations` exists), scoped
 * to the line's selected warehouse — still submitted as a code, same as
 * product/warehouse, so the server-side contract is unchanged. Batch/serial tracking
 * is optional free text here too: no endpoint reports which products
 * have a tracking policy, so this form never claims a product "requires"
 * tracking — a real backend rejection surfaces that fact honestly when
 * it happens, instead of a fabricated pre-check.
 *
 * For issue/transfer, a live "قابل استفاده" hint is shown next to each
 * quantity field, cross-referenced from the already-fetched real
 * stock-balances for that product+warehouse — a genuine pre-submit
 * signal, not a guess, though it can't account for other lines in the
 * same draft consuming the same balance; the real, authoritative check
 * still happens server-side on submit (InsufficientAvailableStockError).
 * The hint also computes a live "مانده پس از خروج/انتقال" (remaining-
 * after) figure from the same real `avail` number, with a one-click
 * "کاهش به X" correction once it goes negative — added 2026-09-17 to
 * match the design's richer hint, no new data source.
 *
 * Transfer's source/destination warehouse match is checked client-side
 * (inline error + submit guard) — added 2026-09-17, matching the
 * design's instant inline error; the server's own rejection remains the
 * authoritative check either way, same "hint client-side, authoritative
 * server-side" pattern as the availability hint above.
 */
export function MovementForm({
  variant,
  onCreated,
}: {
  readonly variant: Variant;
  readonly onCreated: () => void;
}) {
  const { slug } = useParams<{ slug: string }>();
  const base = tenantBasePath(slug ?? '');
  const copy = COPY[variant];
  const showSerial = useTenantSettings().serialTracking;

  const [businessDate, setBusinessDate] = useState('');
  const [partyCode, setPartyCode] = useState('');
  const [sourceReference, setSourceReference] = useState('');
  const [selectedPoRef, setSelectedPoRef] = useState('');
  const [selectedSoRef, setSelectedSoRef] = useState('');
  const [transferMode, setTransferMode] = useState<'two_step' | 'direct'>('two_step');
  const [lines, setLines] = useState<LineDraft[]>([{ ...EMPTY_LINE }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const purchaseOrders = useApiGet<PurchaseOrderListResponse>(
    variant === 'receipt' && slug ? `${base}/api/v1/procurement/orders` : null,
    [slug, variant],
  );

  const salesOrders = useApiGet<SalesOrderListResponse>(
    variant === 'issue' && slug ? `${base}/api/v1/sales/orders` : null,
    [slug, variant],
  );

  const availableOrders = useMemo(() => {
    if (purchaseOrders.status !== 'ready') return [];
    return purchaseOrders.data.orders.filter((o) => {
      const s = orderStatusOf(o.statusLabel);
      return s === 'approved' || s === 'in_transit' || s === 'partially_received';
    });
  }, [purchaseOrders]);

  const availableSalesOrders = useMemo(() => {
    if (salesOrders.status !== 'ready') return [];
    return salesOrders.data.orders.filter(
      (o) => o.status !== 'cancelled' && o.status !== 'draft' && o.status !== 'fulfilled',
    );
  }, [salesOrders]);

  function handleSelectPo(poRef: string) {
    setSelectedPoRef(poRef);
    if (!poRef) {
      setSourceReference('');
      return;
    }
    const po = availableOrders.find((o) => o.ref === poRef);
    if (!po) return;
    setSourceReference(po.ref);
    if (po.supplierPartyRef) {
      setPartyCode(po.supplierPartyRef);
    }

    const outstanding = po.lines.filter((l) => {
      const diff = Number(l.quantity) - Number(l.receivedQuantity || 0);
      return diff > 1e-9;
    });

    if (outstanding.length > 0) {
      setLines(
        outstanding.map((l) => {
          const qty = Number(l.quantity) - Number(l.receivedQuantity || 0);
          return {
            productCode: l.productRef,
            warehouseCode: lines[0]?.warehouseCode || '',
            locationCode: '',
            lotCode: '',
            serialNumber: '',
            quantityNumerator: String(qty),
            unitCostRial: l.unitPriceRial,
            counterWarehouseCode: '',
            counterLocationCode: '',
            poRowNumber: l.rowNumber,
          };
        }),
      );
    }
  }

  function handleSelectSo(soRef: string) {
    setSelectedSoRef(soRef);
    if (!soRef) {
      setSourceReference('');
      return;
    }
    const so = availableSalesOrders.find((o) => o.ref === soRef);
    if (!so) return;
    setSourceReference(so.ref);
    if (so.partyRef) {
      setPartyCode(so.partyRef);
    }

    if (so.fulfillment?.lines?.length) {
      const unfulfilled = so.fulfillment.lines.filter((l) => {
        const remaining = Number(l.remainingQuantity || 0);
        return remaining > 1e-9;
      });
      if (unfulfilled.length > 0) {
        setLines(
          unfulfilled.map((l) => ({
            productCode: l.productRef,
            warehouseCode: lines[0]?.warehouseCode || '',
            locationCode: '',
            lotCode: '',
            serialNumber: '',
            quantityNumerator: String(Number(l.remainingQuantity)),
            unitCostRial: '',
            counterWarehouseCode: '',
            counterLocationCode: '',
          })),
        );
      }
    }
  }

  const parties = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/parties?limit=200` : null,
    [slug],
  );

  const products = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/products?status=active&limit=200` : null,
    [slug],
  );
  const warehouses = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/warehouses?status=active&limit=200` : null,
    [slug],
  );
  const locations = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/locations?status=active&limit=200` : null,
    [slug],
  );
  const balances = useApiGet<StockBalancesResponse>(
    slug ? `${base}/api/v1/inventory/stock-balances?limit=${STOCK_BALANCES_LIMIT}` : null,
    [slug],
  );

  // Available stock per product+warehouse, summed as exact fractions (never through a float).
  const availableFor = useMemo(() => {
    const map = new Map<string, Quantity>();
    if (balances.status === 'ready') {
      for (const b of balances.data.balances) {
        if (!b.productCode || !b.warehouseCode) continue;
        const key = `${b.productCode}::${b.warehouseCode}`;
        const total = addQuantity(
          map.get(key) ?? { numerator: '0', denominator: '1' },
          b.available,
        );
        if (total) map.set(key, total);
      }
    }
    return map;
  }, [balances]);

  function updateLine(index: number, patch: Partial<LineDraft>) {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines((prev) => [...prev, { ...EMPTY_LINE }]);
  }
  function removeLine(index: number) {
    setLines((prev) => prev.filter((_, i) => i !== index));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!businessDate) {
      setError('تاریخ سند را وارد کنید.');
      return;
    }
    // A half-filled row used to be dropped silently, so a receipt with one
    // complete and one incomplete line "saved" without the incomplete one.
    // Name the first incomplete row and field instead.
    const touched = lines
      .map((l, index) => ({ l, index }))
      .filter(({ l }) => l.productCode || l.warehouseCode || l.locationCode || l.quantityNumerator);
    for (const { l, index } of touched) {
      const missing = !l.productCode
        ? 'کالا'
        : !l.warehouseCode
          ? 'انبار'
          : !l.locationCode
            ? 'محل انبار'
            : !l.quantityNumerator
              ? 'مقدار'
              : null;
      if (missing) {
        setError(`ردیف ${index + 1}: فیلد «${missing}» تکمیل نشده است.`);
        return;
      }
    }
    const validLines = touched.map(({ l }) => ({
      ...l,
      quantityNumerator: normalizeDigits(l.quantityNumerator).trim(),
      unitCostRial: normalizeDigits(l.unitCostRial),
    }));
    const wholeNumbersOnly = !(
      variant === 'receipt' &&
      selectedPoRef &&
      validLines.every((l) => l.poRowNumber !== undefined)
    );
    if (wholeNumbersOnly) {
      const bad = validLines.findIndex((l) => !/^\d+$/.test(l.quantityNumerator));
      if (bad >= 0) {
        setError(`ردیف ${bad + 1}: مقدار باید عدد صحیح و نامنفی باشد.`);
        return;
      }
    }
    if (validLines.length === 0) {
      setError('حداقل یک قلم (کالا، انبار، محل انبار و مقدار) لازم است.');
      return;
    }
    if (variant === 'receipt' && validLines.some((l) => !l.unitCostRial.trim())) {
      setError('برای رسید انبار، بهای واحد هر قلم الزامی است.');
      return;
    }
    if (variant === 'receipt' && !partyCode.trim()) {
      setError('انتخاب تأمین‌کننده / طرف‌حساب برای رسید انبار الزامی است.');
      return;
    }
    if (variant === 'issue' && !partyCode.trim()) {
      setError('انتخاب تحویل‌گیرنده / مشتری برای حواله خروج الزامی است.');
      return;
    }
    if (
      variant === 'transfer' &&
      validLines.some((l) => l.counterWarehouseCode && l.warehouseCode === l.counterWarehouseCode)
    ) {
      setError('انبار مبدأ و مقصد نمی‌تواند یکسان باشد.');
      return;
    }

    // If receipt is tied to a PO and all lines have PO line mapping and same warehouse/location:
    if (
      variant === 'receipt' &&
      selectedPoRef &&
      validLines.every((l) => l.poRowNumber !== undefined)
    ) {
      const firstWh = validLines[0]!.warehouseCode;
      const firstLoc = validLines[0]!.locationCode;
      const sameDestination = validLines.every(
        (l) => l.warehouseCode === firstWh && l.locationCode === firstLoc,
      );

      if (sameDestination) {
        setSubmitting(true);
        try {
          await apiPost(
            `${base}/api/v1/procurement/orders/${selectedPoRef}/receipts`,
            {
              warehouseRef: firstWh,
              warehouseLocationRef: firstLoc,
              businessDate,
              lines: validLines.map((l) => ({
                rowNumber: l.poRowNumber!,
                quantity: l.quantityNumerator,
                unitCostRial: l.unitCostRial.trim() || undefined,
              })),
            },
            { headers: { 'idempotency-key': crypto.randomUUID() } },
          );
          setBusinessDate('');
          setPartyCode('');
          setSourceReference('');
          setSelectedPoRef('');
          setSelectedSoRef('');
          setLines([{ ...EMPTY_LINE }]);
          onCreated();
          return;
        } catch (err) {
          setError(bannerMessageFrom(err, 'ثبت رسید سفارش انجام نشد.'));
          return;
        } finally {
          setSubmitting(false);
        }
      }
    }

    setSubmitting(true);
    try {
      await apiPost(
        `${base}/api/v1/inventory/${copy.endpoint}`,
        {
          businessDate,
          partyCode: partyCode.trim() || undefined,
          sourceReference: sourceReference.trim() || undefined,
          ...(variant === 'transfer' ? { transferMode } : {}),
          lines: validLines.map((l) => ({
            productCode: l.productCode,
            warehouseCode: l.warehouseCode,
            locationCode: l.locationCode,
            lotCode: l.lotCode.trim() || undefined,
            serialNumber: l.serialNumber.trim() || undefined,
            quantity: { numerator: l.quantityNumerator, denominator: '1' },
            unitCostRial: l.unitCostRial.trim() || undefined,
            ...(variant === 'transfer'
              ? {
                  counterWarehouseCode: l.counterWarehouseCode,
                  counterLocationCode: l.counterLocationCode,
                }
              : {}),
          })),
        },
        { headers: { 'idempotency-key': crypto.randomUUID() } },
      );
      setBusinessDate('');
      setPartyCode('');
      setSourceReference('');
      setSelectedPoRef('');
      setSelectedSoRef('');
      setLines([{ ...EMPTY_LINE }]);
      onCreated();
    } catch (err) {
      setError(bannerMessageFrom(err, 'ثبت سند انجام نشد.'));
    } finally {
      setSubmitting(false);
    }
  }

  const productOptions = products.status === 'ready' ? products.data.items : [];
  const warehouseOptions = warehouses.status === 'ready' ? warehouses.data.items : [];
  const locationOptions = locations.status === 'ready' ? locations.data.items : [];
  const partyOptions = parties.status === 'ready' ? parties.data.items : [];
  const warehouseIdByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const w of warehouseOptions) map.set(String(w['code']), String(w['warehouseId']));
    return map;
  }, [warehouseOptions]);
  function locationOptionsFor(warehouseCode: string) {
    const warehouseId = warehouseIdByCode.get(warehouseCode);
    if (!warehouseId) return [];
    return locationOptions.filter((l) => String(l['warehouseId']) === warehouseId);
  }

  // Disable + explain a selector instead of leaving it a silent empty
  // dropdown when its master-data list is still loading or genuinely has
  // no rows yet — a bare "— انتخاب —" with nothing under it reads as a
  // form the user did something wrong on, not as "this list is empty".
  // The disabled copy is short on purpose — this select lives in a ~140-
  // 160px table-cell column, and the fuller "…در اطلاعات پایه ثبت نشده"
  // phrasing truncated there (task finding #7); the full sentence is still
  // available via the select's `title` tooltip below.
  const productSelectState =
    products.status === 'loading'
      ? { placeholder: 'در حال بارگذاری…', disabled: true, title: 'در حال بارگذاری کالاها…' }
      : productOptions.length === 0
        ? {
            placeholder: 'کالایی ثبت نشده',
            disabled: true,
            title: 'کالایی در اطلاعات پایه ثبت نشده',
          }
        : { placeholder: '— انتخاب کالا —', disabled: false, title: undefined };
  const warehouseSelectState =
    warehouses.status === 'loading'
      ? { placeholder: 'در حال بارگذاری…', disabled: true, title: 'در حال بارگذاری انبارها…' }
      : warehouseOptions.length === 0
        ? {
            placeholder: 'انباری ثبت نشده',
            disabled: true,
            title: 'انباری در اطلاعات پایه ثبت نشده',
          }
        : { placeholder: '— انتخاب انبار —', disabled: false, title: undefined };

  return (
    <V2Card>
      <V2CardHeader icon="plus" title={copy.title} />
      <form className="inv-form inv-form--card" noValidate onSubmit={onSubmit}>
        <V2FormGrid>
          <V2FormField label="تاریخ سند" required width="medium">
            <JalaliDateInput value={businessDate} onChange={setBusinessDate} />
          </V2FormField>
          {variant === 'receipt' ? (
            <>
              <V2FormField label="تأمین‌کننده / طرف‌حساب" required width="medium">
                <select
                  className="sk-select"
                  value={partyCode}
                  onChange={(e) => setPartyCode(e.target.value)}
                >
                  <option value="">— انتخاب تأمین‌کننده —</option>
                  {partyOptions.map((p) => (
                    <option key={String(p.code)} value={String(p.code)}>
                      {String(p.name ?? p.code)} ({String(p.code)})
                    </option>
                  ))}
                </select>
              </V2FormField>
              <V2FormField label="اتصال به سفارش خرید (PO)" width="wide">
                <select
                  className="sk-select"
                  value={selectedPoRef}
                  onChange={(e) => handleSelectPo(e.target.value)}
                >
                  <option value="">— رسید آزاد (بدون سفارش خرید) —</option>
                  {availableOrders.map((o) => (
                    <option key={o.ref} value={o.ref}>
                      {o.ref} — {o.supplierPartyRef} ({o.statusLabel})
                    </option>
                  ))}
                </select>
              </V2FormField>
            </>
          ) : null}
          {variant === 'issue' ? (
            <>
              <V2FormField label="تحویل‌گیرنده / مشتری" required width="medium">
                <select
                  className="sk-select"
                  value={partyCode}
                  onChange={(e) => setPartyCode(e.target.value)}
                >
                  <option value="">— انتخاب تحویل‌گیرنده / مشتری —</option>
                  {partyOptions.map((p) => (
                    <option key={String(p.code)} value={String(p.code)}>
                      {String(p.name ?? p.code)} ({String(p.code)})
                    </option>
                  ))}
                </select>
              </V2FormField>
              <V2FormField label="اتصال به سفارش فروش (SO)" width="wide">
                <select
                  className="sk-select"
                  value={selectedSoRef}
                  onChange={(e) => handleSelectSo(e.target.value)}
                >
                  <option value="">— خروج آزاد (بدون سفارش فروش) —</option>
                  {availableSalesOrders.map((o) => (
                    <option key={o.ref} value={o.ref}>
                      {o.ref} — {o.partyRef ?? 'مشتری نامشخص'} ({orderStatusLabel(o.status)})
                    </option>
                  ))}
                </select>
              </V2FormField>
            </>
          ) : null}
          {variant === 'transfer' ? (
            <>
              <V2FormField label="نحوه انتقال" width="medium">
                <select
                  className="sk-select"
                  value={transferMode}
                  onChange={(e) => setTransferMode(e.target.value as 'two_step' | 'direct')}
                >
                  <option value="two_step">دو مرحله‌ای (کالای در راه - تأیید در مقصد)</option>
                  <option value="direct">مستقیم و آنی (یک مرحله‌ای)</option>
                </select>
              </V2FormField>
              <V2FormField label="طرف‌حساب / باربری (اختیاری)" width="medium">
                <select
                  className="sk-select"
                  value={partyCode}
                  onChange={(e) => setPartyCode(e.target.value)}
                >
                  <option value="">— انتخاب طرف‌حساب / راننده / باربری —</option>
                  {partyOptions.map((p) => (
                    <option key={String(p.code)} value={String(p.code)}>
                      {String(p.name ?? p.code)} ({String(p.code)})
                    </option>
                  ))}
                </select>
              </V2FormField>
            </>
          ) : null}
          <V2FormField label="مرجع سند" width="medium">
            <input
              className="sk-input"
              value={sourceReference}
              onChange={(e) => setSourceReference(e.target.value)}
              placeholder="اختیاری (مثلاً بارنامه یا حواله)"
            />
          </V2FormField>
        </V2FormGrid>

        {/* "افزودن قلم" sits directly beside the section title it belongs
            to (RTL: same start edge, small gap) instead of being flung to
            the row's opposite edge via space-between — the CTA read as
            disconnected from the line-items table it controls. */}
        <div className="inv-lines-head">
          <div className="inv-cell">
            <span className="inv-cell__main">{copy.itemsTitle}</span>
            <span className="inv-cell__sub">
              کالا، انبار، محل انبار و مقدار برای هر قلم الزامی است.
            </span>
          </div>
          <V2Button type="button" variant="secondary" size="sm" icon="plus" onClick={addLine}>
            افزودن قلم
          </V2Button>
        </div>

        <div className="sk-table-scroll">
          <table className="sk-table sk-table--compact">
            <thead>
              <tr>
                <th className="inv-th-n">#</th>
                {/* `*` marks only the fields onSubmit's own `validLines`
                    filter actually requires (product/warehouse/location/
                    quantity, plus unit cost for a receipt) — the helper
                    line above already says so in words; the markers below
                    previously didn't match it (task finding #6). Counter
                    warehouse/location (transfer) stay unmarked: today's
                    validation doesn't enforce them either, so marking them
                    required would be the same mismatch in the other
                    direction. */}
                <th className="inv-th-product">
                  کالا <span className="sk-form-field__required">*</span>
                </th>
                <th className="inv-th-wh">
                  انبار <span className="sk-form-field__required">*</span>
                </th>
                <th className="inv-th-loc">
                  محل انبار <span className="sk-form-field__required">*</span>
                </th>
                {variant === 'transfer' ? (
                  <>
                    <th className="inv-th-wh">انبار مقصد</th>
                    <th className="inv-th-loc">محل مقصد</th>
                  </>
                ) : null}
                <th className="inv-th-qty">
                  مقدار <span className="sk-form-field__required">*</span>
                </th>
                {variant === 'receipt' ? (
                  <th className="inv-th-cost">
                    بهای واحد (ریال) <span className="sk-form-field__required">*</span>
                  </th>
                ) : null}
                {showSerial ? (
                  <>
                    <th className="inv-th-lot">بچ</th>
                    <th className="inv-th-lot">سریال</th>
                  </>
                ) : null}
                <th className="inv-th-del" aria-label="حذف ردیف" />
              </tr>
            </thead>
            <tbody>
              {lines.map((line, i) => {
                const key = `${line.productCode}::${line.warehouseCode}`;
                const avail = availableFor.get(key);
                return (
                  <tr key={i}>
                    <td className="sk-num">{(i + 1).toLocaleString('fa-IR')}</td>
                    <td>
                      <select
                        className="sk-select inv-cell-control"
                        aria-label={`کالا، ردیف ${i + 1}`}
                        value={line.productCode}
                        disabled={productSelectState.disabled}
                        title={productSelectState.title}
                        onChange={(e) => updateLine(i, { productCode: e.target.value })}
                      >
                        <option value="">{productSelectState.placeholder}</option>
                        {productOptions.map((p) => (
                          <option key={String(p.code)} value={String(p.code)}>
                            {String(p.name ?? p.code)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="sk-select inv-cell-control"
                        aria-label={`انبار، ردیف ${i + 1}`}
                        value={line.warehouseCode}
                        disabled={warehouseSelectState.disabled}
                        title={warehouseSelectState.title}
                        onChange={(e) => updateLine(i, { warehouseCode: e.target.value })}
                      >
                        <option value="">{warehouseSelectState.placeholder}</option>
                        {warehouseOptions.map((w) => (
                          <option key={String(w.code)} value={String(w.code)}>
                            {String(w.name ?? w.code)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="sk-select inv-cell-control"
                        aria-label={`محل انبار، ردیف ${i + 1}`}
                        value={line.locationCode}
                        disabled={!line.warehouseCode}
                        title={!line.warehouseCode ? 'ابتدا انبار را انتخاب کنید' : undefined}
                        onChange={(e) => updateLine(i, { locationCode: e.target.value })}
                      >
                        <option value="">
                          {!line.warehouseCode ? 'ابتدا انبار را انتخاب کنید' : '— انتخاب محل —'}
                        </option>
                        {locationOptionsFor(line.warehouseCode).map((l) => (
                          <option key={String(l['code'])} value={String(l['code'])}>
                            {String(l['name'] ?? l['code'])}
                          </option>
                        ))}
                      </select>
                    </td>
                    {variant === 'transfer' ? (
                      <>
                        <td>
                          <select
                            className="sk-select inv-cell-control"
                            aria-label={`انبار مقصد، ردیف ${i + 1}`}
                            value={line.counterWarehouseCode}
                            disabled={warehouseSelectState.disabled}
                            title={warehouseSelectState.title}
                            onChange={(e) =>
                              updateLine(i, { counterWarehouseCode: e.target.value })
                            }
                          >
                            <option value="">
                              {warehouseSelectState.disabled
                                ? warehouseSelectState.placeholder
                                : '— انتخاب انبار مقصد —'}
                            </option>
                            {warehouseOptions.map((w) => (
                              <option key={String(w.code)} value={String(w.code)}>
                                {String(w.name ?? w.code)}
                              </option>
                            ))}
                          </select>
                          {line.warehouseCode &&
                          line.counterWarehouseCode &&
                          line.warehouseCode === line.counterWarehouseCode ? (
                            <span className="inv-field-error" role="alert">
                              انبار مبدأ و مقصد نمی‌تواند یکسان باشد
                            </span>
                          ) : null}
                        </td>
                        <td>
                          <select
                            className="sk-select inv-cell-control"
                            aria-label={`محل مقصد، ردیف ${i + 1}`}
                            value={line.counterLocationCode}
                            disabled={!line.counterWarehouseCode}
                            title={
                              !line.counterWarehouseCode
                                ? 'ابتدا انبار مقصد را انتخاب کنید'
                                : undefined
                            }
                            onChange={(e) => updateLine(i, { counterLocationCode: e.target.value })}
                          >
                            <option value="">
                              {!line.counterWarehouseCode
                                ? 'ابتدا انبار مقصد را انتخاب کنید'
                                : '— انتخاب محل مقصد —'}
                            </option>
                            {locationOptionsFor(line.counterWarehouseCode).map((l) => (
                              <option key={String(l['code'])} value={String(l['code'])}>
                                {String(l['name'] ?? l['code'])}
                              </option>
                            ))}
                          </select>
                        </td>
                      </>
                    ) : null}
                    <td>
                      <div className="inv-cell">
                        <input
                          className="sk-input ltr sk-num inv-cell-control"
                          inputMode="numeric"
                          aria-label={`مقدار، ردیف ${i + 1}`}
                          value={line.quantityNumerator}
                          onChange={(e) => updateLine(i, { quantityNumerator: e.target.value })}
                        />
                        {(variant === 'issue' || variant === 'transfer') && avail !== undefined
                          ? (() => {
                              const asked = parseWholeQuantity(line.quantityNumerator) ?? {
                                numerator: '0',
                                denominator: '1',
                              };
                              const remaining = subtractQuantity(avail, asked);
                              const negative = remaining ? quantitySign(remaining) < 0 : false;
                              return (
                                <div className="inv-actions">
                                  <span
                                    className={`sk-num inv-cell__sub ${negative ? 'inv-tone-down' : ''}`}
                                  >
                                    قابل استفاده: {formatQuantity(avail)} · مانده پس از{' '}
                                    {variant === 'issue' ? 'خروج' : 'انتقال'}:{' '}
                                    {formatQuantity(remaining)}
                                  </span>
                                  {negative ? (
                                    <button
                                      type="button"
                                      className="sk-btn sk-btn--ghost sk-btn--sm inv-btn-compact"
                                      onClick={() =>
                                        updateLine(i, { quantityNumerator: wholePartText(avail) })
                                      }
                                    >
                                      کاهش به{' '}
                                      {formatQuantity({
                                        numerator: wholePartText(avail) || '0',
                                        denominator: '1',
                                      })}
                                    </button>
                                  ) : null}
                                </div>
                              );
                            })()
                          : null}
                      </div>
                    </td>
                    {variant === 'receipt' ? (
                      <td>
                        <input
                          className="sk-input ltr sk-num inv-cell-control"
                          inputMode="numeric"
                          aria-label={`بهای واحد، ردیف ${i + 1}`}
                          value={line.unitCostRial}
                          onChange={(e) => updateLine(i, { unitCostRial: e.target.value })}
                        />
                      </td>
                    ) : null}
                    {showSerial ? (
                      <>
                        <td>
                          <input
                            className="sk-input ltr inv-cell-control"
                            aria-label={`بچ، ردیف ${i + 1}`}
                            value={line.lotCode}
                            onChange={(e) => updateLine(i, { lotCode: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            className="sk-input ltr inv-cell-control"
                            aria-label={`سریال، ردیف ${i + 1}`}
                            value={line.serialNumber}
                            onChange={(e) => updateLine(i, { serialNumber: e.target.value })}
                          />
                        </td>
                      </>
                    ) : null}
                    <td>
                      {lines.length > 1 ? (
                        <V2Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          iconOnly
                          icon="x"
                          className="inv-btn-danger-text"
                          aria-label={`حذف ردیف ${i + 1}`}
                          onClick={() => removeLine(i)}
                        />
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {variant === 'issue' || variant === 'transfer' ? (
          <V2Alert tone="info">
            اگر ثبت این سند موجودی را منفی کند، سرور آن را رد می‌کند — هیچ گزینهٔ نادیده‌گرفتن یا
            ثبت اجباری وجود ندارد.
          </V2Alert>
        ) : null}

        {error ? <V2Alert tone="danger">{error}</V2Alert> : null}

        <div>
          <V2Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? 'در حال ثبت…' : copy.submitLabel}
          </V2Button>
        </div>
      </form>
    </V2Card>
  );
}
